import React, { useEffect, useMemo, useRef, useState } from 'react';
import './PhoneHandoff.css';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 3000;
// Stop asking after this long; a checkout tab left open overnight shouldn't
// poll forever. Kiosks leave the page on their own idle timer well before.
const POLL_LIMIT_MS = 30 * 60 * 1000;
const SAVE_DEBOUNCE_MS = 700;

const qrSrcFor = (url) => BACKEND + '/qr?d=' + encodeURIComponent(url);

// The checkout URL a phone should open: this page, minus the params that only
// make sense on the screen showing the QR (a phone that scans a kiosk's code
// is an ordinary checkout, not a second kiosk).
export const phoneCheckoutUrl = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('kiosk');
  url.searchParams.delete('returnUrl');
  return url.toString();
};

// Tells this screen when a phone has finished the order. The uploaded image
// URL is unique to the order and rides every path through to /lob, which
// records it on shipping; /order-status answers for it. `paused` is set while
// THIS device is submitting its own order, which would otherwise read as
// "paid elsewhere".
export function useOrderStatusPoll({ active, nonce, imgUrl, paused, onPaid, onQueued }) {
  useEffect(() => {
    if (!active || !nonce || !imgUrl || paused) return undefined;
    let stopped = false;
    let timer = null;
    let queuedReported = false;
    const startedAt = Date.now();

    const poll = async () => {
      if (stopped || Date.now() - startedAt > POLL_LIMIT_MS) return;
      try {
        const res = await fetch(BACKEND + '/order-status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nonce, imgUrl }),
        });
        const data = await res.json();
        if (!stopped && data.paid) {
          onPaid(data);
          return;
        }
        // Paid on the phone but Lob was down: the order is in the retry
        // queue. Say so once, keep polling for `paid`.
        if (!stopped && data.queued && !queuedReported && onQueued) {
          queuedReported = true;
          onQueued(data);
        }
      } catch (e) {
        // Flaky wifi: the next poll tries again.
      }
      timer = setTimeout(poll, POLL_MS);
    };

    timer = setTimeout(poll, POLL_MS);
    return () => { stopped = true; clearTimeout(timer); };
  }, [active, nonce, imgUrl, paused, onPaid, onQueued]);
}

function Panel({ qrUrl, kiosk, className, title, children }) {
  const [qrFailed, setQrFailed] = useState(false);
  if (!qrUrl || qrFailed) return null;
  return (
    <div className={'phone-handoff ' + className + (kiosk ? ' phone-handoff--kiosk' : '')}>
      <img
        className="phone-handoff-qr"
        src={qrSrcFor(qrUrl)}
        alt={title + ' — QR code'}
        onError={() => setQrFailed(true)}
      />
      <div className="phone-handoff-copy">
        <div className="phone-handoff-title">{title}</div>
        <div className="phone-handoff-text">{children}</div>
      </div>
    </div>
  );
}

// Kiosk only: the whole checkout on the buyer's phone. At a fair the point is
// to free the tablet for the next visitor, so this sits above the form --
// scan and walk away. Regular visitors don't get it; nothing should come
// before their form. (They get PayOnPhone at the payment step instead.)
export default function PhoneHandoff() {
  const qrUrl = useMemo(() => phoneCheckoutUrl(), []);
  return (
    <Panel qrUrl={qrUrl} kiosk className="phone-handoff--top" title="Finish on your phone">
      Scan to send this postcard from your own phone, or fill out the form below.
    </Panel>
  );
}

// Payment step: the order is already filled out on this screen; the phone does
// only the paying (their saved cards, Apple/Google Pay). The order is saved
// server-side under a token (it holds an address, so it doesn't go in the QR)
// and re-saved as the buyer edits; one row per PaymentIntent keeps the token,
// and so the QR, stable.
export function PayOnPhone({ paymentIntentId, nonce, order, kiosk, onActive }) {
  const [token, setToken] = useState(null);
  const orderJson = useMemo(() => JSON.stringify(order), [order]);
  const announced = useRef(false);

  useEffect(() => {
    if (!paymentIntentId || !nonce) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await fetch(BACKEND + '/handoff/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nonce, paymentIntentId, order: JSON.parse(orderJson) }),
        });
        const data = await res.json();
        if (!cancelled && data.token) setToken(data.token);
      } catch (e) {
        // No token, no QR; paying on this screen is unaffected.
      }
    }, SAVE_DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(t); };
  }, [paymentIntentId, nonce, orderJson]);

  useEffect(() => {
    if (token && !announced.current && onActive) {
      announced.current = true;
      onActive();
    }
  }, [token, onActive]);

  const qrUrl = token ? window.location.origin + '/?pay=' + token : null;
  return (
    <Panel qrUrl={qrUrl} kiosk={kiosk} className="phone-handoff--pay" title="Pay on your phone">
      Scan to pay with your phone instead &mdash; Apple Pay, Google Pay or a saved card. This screen updates once you&rsquo;ve paid.
    </Panel>
  );
}
